import { useState } from 'react'
import { Loader2 } from 'lucide-react'
import { trialProvisionOutcome } from '@/api/trialProvisionOutcome'
import { postAdminTrialAction } from '@/api/adminTrial'
import Modal from './ui/Modal'

interface Props {
  isOpen: boolean
  onClose: () => void
  onSuccess: (orgId: string) => void
}

type PlanCode = 'accelerate' | 'ignite' | 'command'

interface FormData {
  org_name: string
  owner_name: string
  owner_email: string
  owner_phone: string
  notes: string
  plan_code: PlanCode
  unlimited_trial: boolean
  trial_days: number
}

interface CrmResult {
  contact_id: string | null
  deal_id: string | null
  journey_enrolled: boolean
  warning?: string
}

interface ProvisionSuccess {
  org_id: string
  plan_code?: string
  crm?: CrmResult | null
  crm_error?: string | null
  existing_user?: boolean
  outcome: string
}

const PLAN_OPTIONS: { value: PlanCode; label: string }[] = [
  { value: 'accelerate', label: 'Accelerate' },
  { value: 'ignite', label: 'Ignite' },
  { value: 'command', label: 'Command' },
]

const INITIAL: FormData = {
  org_name: '',
  owner_name: '',
  owner_email: '',
  owner_phone: '',
  notes: '',
  plan_code: 'accelerate',
  unlimited_trial: true,
  trial_days: 30,
}

function planLabel(code: PlanCode): string {
  return PLAN_OPTIONS.find(p => p.value === code)?.label ?? code
}

export default function AddTrialOrgModal({ isOpen, onClose, onSuccess }: Props) {
  const [form, setForm] = useState<FormData>(INITIAL)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [provisionSuccess, setProvisionSuccess] = useState<ProvisionSuccess | null>(null)

  const set = (field: keyof FormData, value: string | boolean | number) =>
    setForm(prev => ({ ...prev, [field]: value }))

  const valid = form.org_name.trim() && form.owner_name.trim() && form.owner_email.trim() && form.owner_phone.trim()

  const finishAndClose = () => {
    const orgId = provisionSuccess?.org_id
    setForm(INITIAL)
    setProvisionSuccess(null)
    setError('')
    if (orgId) onSuccess(orgId)
    else onClose()
  }

  const handleClose = () => {
    if (provisionSuccess) {
      finishAndClose()
      return
    }
    onClose()
  }

  const handleSubmit = async () => {
    if (!valid || submitting || provisionSuccess) return
    setSubmitting(true)
    setError('')

    try {
      const body: Record<string, unknown> = {
        org_name: form.org_name.trim(),
        owner_name: form.owner_name.trim(),
        owner_email: form.owner_email.trim(),
        owner_phone: form.owner_phone.trim(),
        notes: form.notes.trim() || undefined,
        unlimited_trial: form.unlimited_trial,
        plan_code: form.plan_code,
        create_crm_lead: true,
        enroll_marketing_journey: true,
      }
      if (!form.unlimited_trial) {
        body.trial_days = form.trial_days
      }

      const provisionRes = await postAdminTrialAction('admin-provision-trial-org', body)
      const provisionData = await provisionRes.json()
      if (!provisionData.org_id) {
        throw new Error(provisionData.error || provisionData.message || 'Failed to provision trial org')
      }

      const existingUser = provisionData.existing_user === true

      const crmError = provisionData.crm_error ?? null
      const crm = provisionData.crm ?? null
        setProvisionSuccess({
          org_id: provisionData.org_id,
          plan_code: provisionData.plan_code,
          crm,
          crm_error: crmError,
          existing_user: existingUser,
          outcome: trialProvisionOutcome(provisionData),
        })
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setSubmitting(false)
    }
  }

  const inputStyle = { background: '#252836', border: '1px solid #3f4258' }
  const selectedPlan = planLabel(form.plan_code)

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Add Trial Organization" size="md"
      footer={
        provisionSuccess ? (
          <button onClick={finishAndClose}
            className="px-5 py-2.5 rounded-lg text-sm font-medium text-white transition"
            style={{ background: '#6366f1' }}>
            Close
          </button>
        ) : (
          <>
            <button onClick={onClose}
              className="px-4 py-2.5 rounded-lg text-sm transition"
              style={{ background: '#252836', color: '#9ca3af' }}>
              Cancel
            </button>
            <button onClick={handleSubmit} disabled={!valid || submitting}
              className="px-5 py-2.5 rounded-lg text-sm font-medium text-white transition flex items-center gap-2"
              style={{ background: '#6366f1', opacity: valid && !submitting ? 1 : 0.5 }}>
              {submitting && <Loader2 size={14} className="animate-spin" />}
              {submitting ? 'Creating…' : 'Create & Send Welcome'}
            </button>
          </>
        )
      }
    >
      <div className="space-y-4">
        {error && (
          <div className="px-4 py-3 rounded-lg text-sm" style={{ background: '#ef444420', color: '#ef4444', border: '1px solid #ef444440' }}>
            {error}
          </div>
        )}

        {provisionSuccess ? (
          <div className="space-y-3">
            <div className="px-4 py-3 rounded-lg text-sm" style={{ background: '#22c55e20', color: '#4ade80', border: '1px solid #22c55e40' }}>
              {provisionSuccess.outcome}
            </div>
            {(provisionSuccess.crm_error || provisionSuccess.crm?.warning) && (
              <div className="px-4 py-3 rounded-lg text-sm" style={{ background: '#f59e0b20', color: '#fbbf24', border: '1px solid #f59e0b40' }}>
                Organization created. CRM: {provisionSuccess.crm_error || provisionSuccess.crm?.warning}
              </div>
            )}
            {provisionSuccess.crm?.deal_id && (
              <div className="px-4 py-3 rounded-lg text-sm" style={{ background: '#6366f120', color: '#a5b4fc', border: '1px solid #6366f140' }}>
                CRM deal created:{' '}
                <a
                  href={`https://hub.jumpstart.co.il/crm/deals/${provisionSuccess.crm.deal_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:opacity-80"
                >
                  Open deal
                </a>
              </div>
            )}
          </div>
        ) : (
          <>
            <div>
              <label className="block text-sm mb-1.5" style={{ color: '#9ca3af' }}>Organization Name *</label>
              <input type="text" value={form.org_name} onChange={e => set('org_name', e.target.value)}
                placeholder="e.g. Acme Corp"
                className="w-full px-4 py-2.5 rounded-lg text-white text-sm outline-none focus:ring-2 focus:ring-indigo-500"
                style={inputStyle} />
            </div>

            <div>
              <label className="block text-sm mb-1.5" style={{ color: '#9ca3af' }}>Owner Name *</label>
              <input type="text" value={form.owner_name} onChange={e => set('owner_name', e.target.value)}
                placeholder="e.g. Israel Israeli"
                className="w-full px-4 py-2.5 rounded-lg text-white text-sm outline-none focus:ring-2 focus:ring-indigo-500"
                style={inputStyle} />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm mb-1.5" style={{ color: '#9ca3af' }}>Owner Email *</label>
                <input type="email" value={form.owner_email} onChange={e => set('owner_email', e.target.value)}
                  placeholder="email@example.com"
                  className="w-full px-4 py-2.5 rounded-lg text-white text-sm outline-none focus:ring-2 focus:ring-indigo-500"
                  style={inputStyle} />
              </div>
              <div>
                <label className="block text-sm mb-1.5" style={{ color: '#9ca3af' }}>Owner Phone *</label>
                <input type="tel" value={form.owner_phone} onChange={e => set('owner_phone', e.target.value)}
                  placeholder="050-1234567"
                  className="w-full px-4 py-2.5 rounded-lg text-white text-sm outline-none focus:ring-2 focus:ring-indigo-500"
                  style={inputStyle} />
              </div>
            </div>

            <div>
              <label className="block text-sm mb-1.5" style={{ color: '#9ca3af' }}>Notes</label>
              <textarea value={form.notes} onChange={e => set('notes', e.target.value)}
                placeholder="Optional notes about this trial…"
                rows={2}
                className="w-full px-4 py-2.5 rounded-lg text-white text-sm outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                style={inputStyle} />
            </div>

            <div>
              <label className="block text-sm mb-1.5" style={{ color: '#9ca3af' }}>Plan</label>
              <div className="flex rounded-lg overflow-hidden" style={{ border: '1px solid #3f4258' }}>
                {PLAN_OPTIONS.map(opt => {
                  const active = form.plan_code === opt.value
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => set('plan_code', opt.value)}
                      className="flex-1 px-3 py-2.5 text-sm font-medium transition"
                      style={{
                        background: active ? '#6366f1' : '#252836',
                        color: active ? '#ffffff' : '#9ca3af',
                        borderRight: opt.value !== 'command' ? '1px solid #3f4258' : undefined,
                      }}
                    >
                      {opt.label}
                    </button>
                  )
                })}
              </div>
            </div>

            <div className="flex items-center justify-between px-4 py-3 rounded-lg" style={{ background: '#1a1d27', border: '1px solid #2d3148' }}>
              <div>
                <div className="text-sm text-white font-medium">Unlimited Trial</div>
                <div className="text-xs mt-0.5" style={{ color: '#6b7280' }}>
                  {form.unlimited_trial ? 'No expiry — manually control when to end' : 'Expires after 30 days'}
                </div>
              </div>
              <button onClick={() => set('unlimited_trial', !form.unlimited_trial)}
                className="relative flex-shrink-0 w-11 h-6 rounded-full transition-colors duration-200"
                style={{ background: form.unlimited_trial ? '#22c55e' : '#3f4258' }}>
                <span className="absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform duration-200"
                  style={{ transform: form.unlimited_trial ? 'translateX(20px)' : 'translateX(0)' }} />
              </button>
            </div>

            {!form.unlimited_trial && (
              <div>
                <label className="block text-sm mb-1.5" style={{ color: '#9ca3af' }}>Trial days</label>
                <input
                  type="number"
                  min={1}
                  max={365}
                  value={form.trial_days}
                  onChange={e => {
                    const n = Number(e.target.value)
                    if (Number.isNaN(n)) return
                    set('trial_days', Math.min(365, Math.max(1, n)))
                  }}
                  className="w-full px-4 py-2.5 rounded-lg text-white text-sm outline-none focus:ring-2 focus:ring-indigo-500"
                  style={inputStyle}
                />
              </div>
            )}

            <div className="text-xs px-1" style={{ color: '#6b7280' }}>
              Creates: org + auth user + {selectedPlan} subscription + all modules + CRM lead &amp; &quot;מנוי מערכת&quot; deal in Otomator.
              <br />
              Sends: welcome email + official WhatsApp with a personal login link, then starts the 30-day trial marketing journey.
            </div>
          </>
        )}
      </div>
    </Modal>
  )
}
